/**
 * Data layer for `branding`. Thin typed fetch wrappers over /api/v1/branding.
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 *
 * Backend surface (apps/api/src/routes/branding.ts):
 *   GET  /api/v1/branding                       → current tenant branding
 *   PUT  /api/v1/branding                       → { brand_color?, logo_r2_key? }
 *   POST /api/v1/branding/logo                  → signed upload target
 *   PUT  /api/v1/branding/logo/upload?key=&...  → raw logo bytes
 */

export interface BrandingSettings {
	brand_color: string;
	logo_r2_key: string | null;
	logo_content_type: string | null;
	logo_url: string | null;
	is_default: boolean;
	// Portal name is surfaced on the form but is persisted locally (backend
	// does not currently expose a tenant `portal_name` column for branding).
	// We stash it in a lightweight localStorage entry keyed per origin so the
	// preview card can render something sensible for the founder.
	portal_name?: string;
}

export interface UpdateBrandingBody {
	brand_color?: string | null;
	logo_r2_key?: string | null;
}

export interface LogoSignResponse {
	upload_url: string;
	r2_key: string;
	expires_at: number;
	token: string;
	max_bytes: number;
	method: "PUT";
	content_type: string;
}

export interface LogoUploadResponse {
	ok: boolean;
	r2_key: string;
	content_type: string;
}

const BASE = "/api/v1/branding";
const PORTAL_NAME_LS_KEY = "trackerity.branding.portal_name";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function get(): Promise<BrandingSettings> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	const data = await parseOrThrow<BrandingSettings>(res);
	// Fold localStorage portal_name in so the UI gets a single object.
	try {
		if (typeof localStorage !== "undefined") {
			const pn = localStorage.getItem(PORTAL_NAME_LS_KEY);
			if (pn) data.portal_name = pn;
		}
	} catch {
		// localStorage access denied — quietly ignore.
	}
	return data;
}

export async function update(body: UpdateBrandingBody): Promise<BrandingSettings> {
	const res = await fetch(BASE, {
		method: "PUT",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<BrandingSettings>(res);
}

export async function signLogoUpload(
	contentType: string,
	contentLength: number,
): Promise<LogoSignResponse> {
	const res = await fetch(`${BASE}/logo`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify({
			content_type: contentType,
			content_length: contentLength,
		}),
	});
	return parseOrThrow<LogoSignResponse>(res);
}

export async function uploadLogoBytes(
	uploadUrl: string,
	file: File | Blob,
): Promise<LogoUploadResponse> {
	const res = await fetch(uploadUrl, {
		method: "PUT",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": file.type || "application/octet-stream",
		},
		body: file,
	});
	return parseOrThrow<LogoUploadResponse>(res);
}

/**
 * Convenience: sign + upload + PUT /branding attaching the new key.
 * Returns the freshly merged BrandingSettings.
 */
export async function replaceLogo(file: File): Promise<BrandingSettings> {
	const sign = await signLogoUpload(file.type, file.size);
	await uploadLogoBytes(sign.upload_url, file);
	return update({ logo_r2_key: sign.r2_key });
}

export function savePortalName(name: string): void {
	try {
		if (typeof localStorage === "undefined") return;
		if (name) localStorage.setItem(PORTAL_NAME_LS_KEY, name);
		else localStorage.removeItem(PORTAL_NAME_LS_KEY);
	} catch {
		// localStorage quota / disabled — silent.
	}
}

/**
 * Validators — colocated for the form. Hex color MUST be 6-char with `#`.
 */
export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export function isValidHexColor(v: unknown): v is string {
	return typeof v === "string" && HEX_COLOR_RE.test(v);
}

export const ALLOWED_LOGO_TYPES = [
	"image/png",
	"image/jpeg",
	"image/jpg",
	"image/svg+xml",
	"image/webp",
] as const;

export const ALLOWED_FAVICON_TYPES = [
	"image/png",
	"image/x-icon",
	"image/vnd.microsoft.icon",
	"image/svg+xml",
] as const;

export const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB
export const MAX_FAVICON_BYTES = 256 * 1024; // 256 KB

export function validateLogoFile(
	file: File,
): { ok: true } | { ok: false; error: string } {
	if (!ALLOWED_LOGO_TYPES.includes(file.type as (typeof ALLOWED_LOGO_TYPES)[number])) {
		return { ok: false, error: "unsupported logo type" };
	}
	if (file.size > MAX_LOGO_BYTES) {
		return { ok: false, error: "logo exceeds 2 MB" };
	}
	return { ok: true };
}

export function validateFaviconFile(
	file: File,
): { ok: true } | { ok: false; error: string } {
	if (
		!ALLOWED_FAVICON_TYPES.includes(
			file.type as (typeof ALLOWED_FAVICON_TYPES)[number],
		)
	) {
		return { ok: false, error: "unsupported favicon type" };
	}
	if (file.size > MAX_FAVICON_BYTES) {
		return { ok: false, error: "favicon exceeds 256 KB" };
	}
	return { ok: true };
}

export const brandingQueryKeys = {
	all: ["branding"] as const,
	settings: () => [...brandingQueryKeys.all] as const,
};
